import { Link } from "react-router";
import {
  homeworkOverdue,
  homeworkState,
  type HomeworkState,
  type HomeworkSummary,
} from "@edu/shared";
import { dueLabel } from "../../../lib/when.ts";
import { reviewPlacement } from "./review-flag.ts";
import { LATE_TEXT, materialCell } from "./material-cell.ts";

/**
 * Строка списка работ: Ученик | Работа | Срок | Верно | Состояние.
 *
 * ⚠️ Слоты фиксированы, и это главное свойство строки. Прежняя карточка
 * собирала подпись из того, что не `null` (`meta.join(" · ")`), поэтому срок у
 * одной работы стоял первым, а у соседней третьим, и колоночного чтения не
 * получалось. Теперь пустых слотов не бывает: нет срока, стоит «срока нет»,
 * нет заданий, стоит «нет заданий».
 *
 * ⚠️ В разрезе по ученику колонка имени убирается у ВСЕХ строк сразу, а
 * контейнер переключается на `list--hw-solo`: число колонок в разметке обязано
 * совпадать с числом колонок в `--cols`. Само имя стоит в шапке разреза, и
 * повторять его двенадцать раз это шум (5.2 фазы 2).
 *
 *
 * Ссылка несёт адрес, с которого пришли: из карточки работы крошка ведёт назад в
 * ТОТ ЖЕ разрез, а не в общий список. Иначе преподаватель, проверяющий двенадцать
 * работ подряд, двенадцать раз выбирает ученика заново (5.1 фазы 2).
 */

export function HomeworkRow(props: {
  homework: HomeworkSummary;
  hideCounterpart: boolean;
  backTo: string;
  /** Один `Date.now()` на весь список, а не свой на каждую строку. */
  now: number;
}) {
  const h = props.homework;
  const state = homeworkState({ firstOpenedAt: h.firstOpenedAt, counters: h.counters });
  // Клиентские часы участвуют только в подписи, где минуты расхождения
  // безобидны (4.3 проекта); права по ним не решаются нигде
  const overdue = homeworkOverdue({ dueAt: h.dueAt, state, now: props.now });
  // Третий аргумент гасит красноту у сданных: у выполненной работы прошедший
  // срок это факт биографии, а не тревога, иначе список выполненных горит весь
  const due = dueLabel(h.dueAt, props.now, state !== "done");
  const { solved, attempted, total, gradable } = h.counters;
  const flag = reviewPlacement(h.review, h.myRole, state);

  return (
    <li className="row">
      {!props.hideCounterpart && (
        <span
          className="cell"
          data-label={h.myRole === "teacher" ? "Ученик" : "Преподаватель"}
        >
          {h.counterpartName ?? "…"}
        </span>
      )}

      <span className="cell row__name">
        <Link to={`/homework/${h.id}`} state={{ from: props.backTo }}>
          {h.title}
        </Link>
        {/* Единственная необязательная деталь строки, и она живёт ВНУТРИ главной
            ячейки: колонки от неё не двигаются, появилась она или нет */}
        {h.revealedAt !== null && <span className="row__sub">правильные ответы показаны</span>}
      </span>

      <span
        className="cell row__when"
        data-label="Срок"
        data-narrow="keep"
        data-tone={due.tone}
        title={due.title}
      >
        {due.text}
      </span>

      {/* Счётчик занят всегда, в том числе нулём: раньше он появлялся только
          после первой попытки, и колонка прыгала от строки к строке */}
      {total === 0 ? (
        /*
          Домашка без заданий (docs/homework-scratch-design.md, 1.5): вместо
          «нет заданий» то, что отправил ученик. ⚠️ С 13.10.2026 у ученика и
          родителя та же ячейка (решение владельца): «нет заданий» не отвечало
          на вопрос, сдана ли домашка. Разница одна, «новых» у них нет, и
          держится она формой данных (`newPhotos` и `newAnswer` ученику не едут)
        */
        <MaterialNum homework={h} />
      ) : gradable === 0 ? (
        /*
          ⚠️ Вся работа из заданий без автопроверки (перевод, 4.3
          docs/translate-exercise-design.md): доли «верно» нет вовсе. «Верно
          0/1» у сделанного перевода увидели бы ученик и родитель, а оценивать
          его будет преподаватель
        */
        <span className="cell row__num" data-label="Отвечено" data-quiet={attempted === 0}>
          отвечено {attempted} из {total}
          <span className="row__bar">
            <i className="row__bar--tried" style={{ width: pct(attempted, total) }} />
          </span>
        </span>
      ) : (
        <span className="cell row__num" data-label="Верно" data-quiet={solved === 0}>
          {/* Доля «верно» делится на задания с автопроверкой, а не на все: у
              перевода вердикта нет, и в знаменателе он читался бы ошибкой */}
          {solved}/{gradable}
          {/*
            Полоса двухслойная: бледный слой это отвеченное, плотный верное.
            Слои вложены, а не поставлены рядом, потому что отвеченное всегда
            не меньше верного, и «рядом» пришлось бы считать разность, то есть
            выводить на экран величину, которой никто не спрашивал.
          */}
          <span className="row__bar">
            <i className="row__bar--tried" style={{ width: pct(attempted, total) }} />
            {/* Плотный слой делится на задания с автопроверкой, как и число
                над ним: перевод в знаменателе читался бы ошибкой. Бледный
                остаётся от всех заданий, отвечают и на перевод */}
            <i style={{ width: pct(solved, gradable) }} />
          </span>
          {/*
            Второе число, просьба преподавателя 20.08.2026 дословно: «дано 7
            ответов, из них 5 правильных, а всего 15». Без него список отвечал
            на вопрос «сколько он решил верно» и молчал про «сколько он вообще
            прощёлкал», то есть про то, закончил человек или ушёл гулять.

            ⚠️ Ирония места: пилюля состояния СПРАВА считается ровно из этого
            числа (`homeworkState`: отвечено на все это «сдана»), то есть
            единственной непоказанной величиной строки была та, которой строка
            и подписана.
          */}
          <span className="row__sub">отвечено {attempted}</span>
        </span>
      )}

      {/*
        ⚠️ Пилюли «просрочена» здесь нет, и это не потеря. До 14.09.2026 в одной
        строке стояли красный текст «просрочено 23 дня» в колонке СРОК и сплошная
        красная пилюля «просрочена» в колонке СОСТОЯНИЕ, то есть один факт двумя
        средствами, и вместе они красили половину списка.

        ⚠️ Но состояние у просроченной работы ЕСТЬ, и оно другое. «Просрочено 23
        дня» говорит про срок, а «не открыта» и «в работе» про ученика, и
        преподаватель решает по второму: к не открытой возвращаются на уроке, а
        начатую доводят. Первая редакция этой правки слот просто прятала, и тогда
        при фильтре «Просрочено» колонка пустела целиком, а её заголовок висел
        над пустотой.
      */}
      {/*
        Признак проверки перевода заменяет плашку состояния или встаёт хвостом
        (5.3 docs/translate-review-design.md): «сдана» рядом с «ждёт проверки»
        говорила бы про одну домашку два разных ответа. Когда что, решает
        `reviewPlacement`, одна на строку и на шапку ленты ученика
      */}
      <span className="cell" data-label="Состояние">
        {flag?.replaces === true ? (
          <span className="pill" data-tone={flag.tone}>
            {flag.text}
          </span>
        ) : (
          <span className="pill" data-tone={state === "done" ? "ok" : undefined}>
            {stateText(state)}
            {flag !== null && ` · ${flag.text}`}
          </span>
        )}
      </span>
    </li>
  );
}

/** Слот «Верно» у домашки без заданий: фото, ответ текстом, отметка ученика, просрочка сдачи. */
function MaterialNum(props: { homework: HomeworkSummary }) {
  const h = props.homework;
  const cell = materialCell({
    counters: h.counters,
    newPhotos: h.newPhotos,
    newAnswer: h.newAnswer,
    dueAt: h.dueAt,
  });
  return (
    <span className="cell row__num" data-label="Ответы" data-quiet={cell.quiet}>
      {cell.text}
      {cell.late && <span className="row__sub">{LATE_TEXT}</span>}
    </span>
  );
}

/**
 * Состояние словом.
 *
 * ⚠️ Просрочки здесь нет намеренно: про срок говорит колонка СРОК, а эта
 * колонка говорит про ученика. Тона два, зелёный у сделанной и никакого у
 * остальных: «не открыта» это работа, выданная час назад, и действия сегодня
 * она не требует.
 */
function stateText(state: HomeworkState): string {
  if (state === "done") return "сдана";
  return state === "not_opened" ? "не открыта" : "в работе";
}

/** Доля величины от общего в процентах строкой для `style`. */
function pct(value: number, total: number): string {
  return `${Math.round((value / total) * 100)}%`;
}
